import { type FC, useContext, useEffect, useState } from 'react';
import { FormattedMessage, defineMessages, useIntl, type IntlShape } from 'dibs-react-intl';
import { useFragment, graphql } from 'react-relay';
import classnames from 'classnames';

import { useSupportChat } from 'dibs-support-chat/exports/useSupportChat.ts';

import PersonBubble from 'dibs-icons/exports/legacy/PersonBubble.ts';
import Message from 'dibs-icons/exports/legacy/Message.ts';
import WhatsApp from 'dibs-icons/exports/legacy/WhatsApp.ts';
import InfoCircle from 'dibs-icons/exports/legacy/InfoCircle.ts';
import { Tooltip } from 'dibs-elements/exports/Tooltip.tsx';
import SupportChannelLink from './SupportChannelLink.tsx';
import SendMessageButton from './SendMessageButton.tsx';

import GetHelpContext from './GetHelpContext.ts';
import {
    CHANNEL_LIVE_CHAT,
    CHANNEL_WHATSAPP,
    CHANNEL_MESSAGE,
    RENDER_AS_LOGGED_OUT,
    HELP_TYPE_LISTINGS,
    HELP_TYPE_ORDER_ISSUES,
} from './constants.ts';
import { type HelpType } from './types.ts';
import {
    trackChatInitiated,
    trackWhatsAppClick,
    trackSMSClick,
    trackLiveChatDisplayed,
} from './tracking.ts';

import { type OtherHelp_viewer$key } from './__generated__/OtherHelp_viewer.graphql.ts';

import dibsCss from 'dibs-css';
import styles from './styles/OtherHelp.scss';
import { getSupportNumber } from './helpers.tsx';

const messages = defineMessages({
    liveChatTitle: {
        id: 'getHelp.OtherHelp.liveChatTitle',
        defaultMessage: 'Live Chat',
    },
    sendMessageAvailability: {
        id: 'getHelp.OtherHelp.sendMessageAvailability',
        defaultMessage: 'Available 24/7',
    },
    liveChatAvailabilityOnline: {
        id: 'getHelp.OtherHelp.liveChatAvailabilityOnline',
        defaultMessage: 'Online',
    },
    whatsApp: {
        id: 'getHelp.OtherHelp.whatsApp',
        defaultMessage: 'WhatsApp',
    },
    textMessage: {
        id: 'getHelp.OtherHelp.textMessage',
        defaultMessage: 'Text Message',
    },
    waitUntilOnline: {
        id: 'getHelp.OtherHelp.waitUntilOnline',
        defaultMessage: 'Back online in {hours, plural, one {# hour} other {# hours}}',
    },
    waitTime: {
        id: 'getHelp.OtherHelp.waitTime',
        defaultMessage: 'Wait time {hours, plural, one {# hour} other {# hours}}',
    },
    defaultMesssageText: {
        id: 'getHelp.OtherHelp.defaultMesssageText',
        defaultMessage: 'Hello, I need help with Order {orderId}',
    },
    defaultMesssageTextForItem: {
        id: 'getHelp.OtherHelp.defaultMesssageTextForItem',
        defaultMessage: 'Hello, I need help with Item {itemId}',
    },
});

export const getAvailability = ({
    isAgentAvailable,
    isChat = false,
    intl,
}: {
    isAgentAvailable: boolean;
    isChat?: boolean;
    intl: IntlShape;
}): string | null => {
    // everything is counted in UTC format because Support Reps are working in GMT time. (Monday - Friday, 8:00 - 22:00 GMT)

    const WORK_DAY_START_HOURS = 8;
    const WORK_DAY_END_HOURS = 22;
    const WORK_WEEK_START_DAY = 1;
    const WORK_WEEK_END_DAY = 5;
    const DEFAULT_WAITING_HOURS = '2';

    const day = new Date().getUTCDay();
    const hours = new Date().getUTCHours();

    const isWorkWeek = day >= WORK_WEEK_START_DAY && day <= WORK_WEEK_END_DAY;
    const isWorkingHours = hours >= WORK_DAY_START_HOURS && hours <= WORK_DAY_END_HOURS;
    const isLastWorkDay = day === WORK_WEEK_END_DAY;

    if (isAgentAvailable) {
        return intl.formatMessage(messages.liveChatAvailabilityOnline);
    }

    if (isWorkWeek && !isWorkingHours) {
        if (isLastWorkDay && hours > WORK_DAY_END_HOURS) {
            return null;
        }

        const now = new Date();

        const startOfWorkingDay = Date.UTC(
            now.getUTCFullYear(),
            now.getUTCMonth(),
            now.getUTCDate() + (hours >= WORK_DAY_END_HOURS ? 1 : 0),
            WORK_DAY_START_HOURS,
            0,
            0
        );

        // to get time difference in hours milliseconds needs to be converted in hours by dividing it by 60*60*1000 = 3600000
        const difference = Math.ceil(
            Math.abs(new Date(startOfWorkingDay).getTime() - new Date().getTime()) / 3600000
        ).toString();

        if (isChat) {
            return intl.formatMessage(messages.waitUntilOnline, { hours: difference });
        } else {
            return intl.formatMessage(messages.waitTime, { hours: difference });
        }
    } else if (isWorkWeek && isWorkingHours) {
        if (isChat) {
            return intl.formatMessage(messages.waitUntilOnline, { hours: DEFAULT_WAITING_HOURS });
        } else {
            return intl.formatMessage(messages.waitTime, { hours: DEFAULT_WAITING_HOURS });
        }
    }

    return null;
};

const getDefaultMessageText = ({
    orderId,
    itemId,
    helpType,
    intl,
}: {
    orderId?: string;
    itemId?: string;
    helpType?: HelpType | null;
    intl: IntlShape;
}): string => {
    let message = '';

    if (helpType === HELP_TYPE_LISTINGS && !!itemId) {
        message = intl.formatMessage(messages.defaultMesssageTextForItem, { itemId });
    }

    if (helpType === HELP_TYPE_ORDER_ISSUES && !!orderId) {
        message = intl.formatMessage(messages.defaultMesssageText, { orderId });
    }

    return encodeURI(message);
};

const TextMessageInfo = (
    <FormattedMessage
        id="getHelp.OtherHelp.tooltip"
        defaultMessage="Message and data rates may apply."
    />
);

const OtherHelp: FC<{
    viewer: OtherHelp_viewer$key;
    scrollToOtherHelp?: boolean;
    setScrollToOtherHelp?: (scroll: boolean) => void;
}> = ({ viewer: viewerRef, scrollToOtherHelp, setScrollToOtherHelp }) => {
    const intl = useIntl();
    const [isTooltipVisible, setIsTooltipVisible] = useState(false);

    const viewer = useFragment(
        graphql`
            fragment OtherHelp_viewer on Viewer {
                getHelpTopic(topicKey: $topicKey, orderId: $orderId, isSeller: $isSeller) {
                    showChannelLiveChat
                    showChannelWhatsApp
                    showChannelMessage
                    showChannelEmail
                    isOtherIssue
                }
                loggedInBuyerSupportChat: supportChat(touchpoint: Buyer_Messaging)
                    @include(if: $hasUserId) {
                    ...useSupportChat_supportChat
                }
                loggedOutBuyerSupportChat: supportChat(touchpoint: Guest_Buyer_Messaging)
                    @include(if: $isLoggedOutUser) {
                    ...useSupportChat_supportChat
                }
                sellerSupportChat: supportChat(touchpoint: Seller_Messaging)
                    @include(if: $isSeller) {
                    ...useSupportChat_supportChat
                }
                user(userId: $userId) @include(if: $hasUserId) {
                    ...useSupportChat_user
                }
                seller(sellerId: $sellerId) @include(if: $hasSellerId) {
                    ...useSupportChat_seller
                }
            }
        `,
        viewerRef
    );

    const {
        state: {
            showAuthModal,
            renderAs,
            topicValue,
            orderId,
            itemPk,
            isEmbedded,
            onClose,
            helpType,
            isSeller,
            placement,
        },
        dispatch,
    } = useContext(GetHelpContext);

    useEffect(() => {
        if (scrollToOtherHelp) {
            const modalElement = document.querySelectorAll("[data-tn='get-help-modal']")[0];

            if (modalElement) {
                modalElement.scrollTop = modalElement.scrollHeight;
            }

            if (setScrollToOtherHelp) {
                setScrollToOtherHelp(false);
            }
        }
    }, [scrollToOtherHelp, setScrollToOtherHelp]);

    const {
        getHelpTopic,
        loggedInBuyerSupportChat,
        loggedOutBuyerSupportChat,
        sellerSupportChat,
        user,
        seller,
    } = viewer;
    const buyerSupportChat = user ? loggedInBuyerSupportChat : loggedOutBuyerSupportChat;

    const {
        showChannelLiveChat,
        showChannelMessage,
        showChannelWhatsApp,
        showChannelEmail,
        isOtherIssue,
    } = getHelpTopic || {};

    const {
        isEnabled: isChatEnabled,
        isChatAvailable,
        isLiveAgentAvailable,
        initSupportChat,
    } = useSupportChat({
        supportChat: isSeller ? sellerSupportChat : buyerSupportChat,
        ...(isSeller ? { seller } : { user }),
    });

    const availabilityCopy = getAvailability({ isAgentAvailable: isLiveAgentAvailable, intl });

    useEffect(() => {
        if (!showChannelLiveChat || !availabilityCopy) {
            return;
        }

        trackLiveChatDisplayed({ placement, isAgentAvailable: isChatAvailable });
    }, [showChannelLiveChat, availabilityCopy, placement, isChatAvailable]);

    // don't render component if all channels are disabled
    if (!(showChannelLiveChat || showChannelMessage || showChannelWhatsApp || showChannelEmail)) {
        return null;
    }

    return (
        <div className={classnames(styles.container, dibsCss.pLarge, dibsCss.pbLarger)}>
            {!isOtherIssue && (
                <div className={styles.title}>
                    <FormattedMessage
                        id="getHelp.OtherHelp.title"
                        defaultMessage="Still need help?"
                    />
                </div>
            )}
            {availabilityCopy && (
                <div className={styles.supportLinks}>
                    {showChannelLiveChat && (
                        <SupportChannelLink
                            title={intl.formatMessage(messages.liveChatTitle)}
                            Icon={PersonBubble}
                            onClick={() => {
                                if (renderAs === RENDER_AS_LOGGED_OUT) {
                                    if (showAuthModal) {
                                        showAuthModal({
                                            topicValue: topicValue || '',
                                            channel: CHANNEL_LIVE_CHAT,
                                            isChatFlow: true,
                                            helpType,
                                            onSuccess: () => dispatch({ type: 'SET_BUYER' }),
                                        });
                                    }
                                } else {
                                    initSupportChat({
                                        openWindow: true,
                                        showChatButtonOnLoad: false,
                                        touchpoint: isSeller
                                            ? 'Seller_Messaging'
                                            : 'Buyer_Messaging',
                                        locale: intl.locale,
                                        preChatFields: {
                                            pageType: 'get help',
                                            topic:
                                                topicValue ||
                                                (isSeller
                                                    ? 'sellingGeneralQuestions'
                                                    : 'itemQuestions'),
                                            itemId: itemPk,
                                            orderId,
                                        },
                                    });

                                    trackChatInitiated(topicValue || '');

                                    if (!isEmbedded) {
                                        onClose();
                                    }
                                }
                            }}
                            availability={
                                getAvailability({
                                    isAgentAvailable: isChatAvailable,
                                    isChat: true,
                                    intl,
                                }) || ''
                            }
                            showOnline={isChatAvailable}
                            isAgentAvailable={isChatAvailable}
                            disabled={!isChatEnabled}
                            channel={CHANNEL_LIVE_CHAT}
                        />
                    )}
                    {showChannelMessage && (
                        <SupportChannelLink
                            title={
                                <div className={styles.linkTitle}>
                                    {intl.formatMessage(messages.textMessage)}
                                    <span className={styles.asterisk}>*</span>
                                    <span
                                        onMouseEnter={() => setIsTooltipVisible(true)}
                                        onMouseLeave={() => setIsTooltipVisible(false)}
                                        className={styles.tooltipWrapper}
                                    >
                                        <InfoCircle className={styles.tooltipIcon} />
                                        <Tooltip
                                            isVisible={isTooltipVisible}
                                            align="center"
                                            direction="top"
                                            onClose={() => setIsTooltipVisible(false)}
                                        >
                                            {TextMessageInfo}
                                        </Tooltip>
                                    </span>
                                </div>
                            }
                            Icon={Message}
                            channel={CHANNEL_MESSAGE}
                            availability={availabilityCopy}
                            showOnline={isLiveAgentAvailable}
                            href={`sms:+${getSupportNumber()}?&body=${getDefaultMessageText({
                                orderId,
                                itemId: itemPk,
                                helpType,
                                intl,
                            })}`}
                            onClick={() => trackSMSClick(topicValue || '')}
                        />
                    )}
                    {showChannelWhatsApp && (
                        <SupportChannelLink
                            title={intl.formatMessage(messages.whatsApp)}
                            Icon={WhatsApp}
                            channel={CHANNEL_WHATSAPP}
                            availability={availabilityCopy}
                            showOnline={isLiveAgentAvailable}
                            href={`https://wa.me/${getSupportNumber()}?text=${getDefaultMessageText(
                                {
                                    orderId,
                                    itemId: itemPk,
                                    helpType,
                                    intl,
                                }
                            )}`}
                            onClick={() => trackWhatsAppClick(topicValue || '')}
                        />
                    )}
                </div>
            )}
            {showChannelEmail && <SendMessageButton />}
            {showChannelMessage && <div className={styles.textMessageInfo}>*{TextMessageInfo}</div>}
        </div>
    );
};

export default OtherHelp;
